import { Box, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import CompanyForm from '../../components/settings/CompanyForm/CompanyForm';
import UserForm from '../../components/settings/UserForm/UserForm';
import BillingSupportLinks from '../../components/shared/BillingSupportLinks/BillingSupportLinks';
import StyledButton from '../../components/shared/Button/StyledButton';
import EmbedSnippet from '../../components/shared/EmbedSnippet/EmbedSnippet';
import Modal from '../../components/shared/Modal/Modal';
import Page from '../../components/shared/Page/Page';
import { AnalyticsEvents } from '../../enums/AnalyticsEvents';
import { useCompanyQuery } from '../../hooks/queries/useCompany';
import { useUser } from '../../hooks/queries/useUser';
import { useAnalytics } from '../../hooks/useAnalytics';
import useAuth from '../../hooks/useAuth';
import { useSnackBar } from '../../hooks/useSnackBar';
import { useTranslation } from '../../hooks/useTranslation';
import { DeviceWidth, ResponseObject } from '../../interfaces/App';
import { getCompanyAgents, updateAgent } from '../../services/agentService';
import { hasPartnerSession } from '../../services/partnerSession';
import { updateCompany } from '../../services/companyService';
import { updateUser } from '../../services/userService';

interface UserFormData {
  user_name: string;
  user_email: string;
  user_phone: string;
  user_role: string;
}

interface CompanyFormData {
  company_name: string;
  company_email: string;
  company_phone: string;
}

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const isPartnerSession = hasPartnerSession();
  const displayMessage = useSnackBar();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  // Analytics state
  const [companyFormStarted, setCompanyFormStarted] = useState(false);
  const [companyFormSaved, setCompanyFormSaved] = useState(false);
  const [companyFormChangedFields, setCompanyFormChangedFields] = useState<
    Set<string>
  >(new Set());

  // Fetch user data using React Query
  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
  } = useUser(currentUser?.id, !!currentUser);

  // Fetch company data using React Query
  const {
    data: companyData,
    isLoading: isCompanyLoading,
    error: companyError,
  } = useCompanyQuery(
    userData?.message?.company_id,
    !!userData?.message?.company_id
  );

  const isLoading = isUserLoading || isCompanyLoading;

  const isCompanyAdmin = Boolean(
    currentUser?.permissions?.admin || currentUser?.permissions?.super_admin
  );

  const userFormMethods = useForm<UserFormData>({
    defaultValues: {
      user_name: '',
      user_email: '',
      user_phone: '',
      user_role: '',
    },
  });

  const companyFormMethods = useForm<CompanyFormData>({
    defaultValues: {
      company_name: '',
      company_email: '',
      company_phone: '',
    },
  });

  // Handle user data errors
  useEffect(() => {
    if (userError) {
      console.error(userError);
      displayMessage(t('Unable to load user data'), 'error');
    }
  }, [userError, displayMessage, t]);

  // Handle company data errors
  useEffect(() => {
    if (companyError) {
      console.error(companyError);
      displayMessage(t('Unable to load user and company data'), 'error');
    }
  }, [companyError, displayMessage, t]);

  // Reset user form when user data is loaded
  useEffect(() => {
    if (userData?.message) {
      userFormMethods.reset({
        user_name: userData.message.name ?? '',
        user_email: userData.message.email ?? '',
        user_phone: userData.message.phone ?? '',
        user_role: userData.message.role ?? '',
      });
    }
  }, [userData, userFormMethods]);

  // Reset company form when company data is loaded
  useEffect(() => {
    if (companyData?.message) {
      companyFormMethods.reset({
        company_name: companyData.message.name ?? '',
        company_email: companyData.message.email ?? '',
        company_phone: companyData.message.phone ?? '',
      });
    } else if (userData?.message && !userData.message.company_id) {
      companyFormMethods.reset({
        company_name: '',
        company_email: '',
        company_phone: '',
      });
    }
  }, [companyData, userData, companyFormMethods]);

  // Watch for company form changes
  useEffect(() => {
    const subscription = companyFormMethods.watch((value, { name }) => {
      if (!companyFormStarted && companyFormMethods.formState.isDirty) {
        setCompanyFormStarted(true);
        track(AnalyticsEvents.COMPANY_DETAILS_COMPLETION_STARTED);
      }
      if (name) {
        setCompanyFormChangedFields(prev => {
          if (!prev.has(name)) {
            track(AnalyticsEvents.COMPANY_DETAILS_COMPLETION_FIELD_UPDATED, {
              field: name,
            });
            const newSet = new Set(prev);
            newSet.add(name);
            return newSet;
          }
          return prev;
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [companyFormMethods, companyFormStarted, track]);

  // Track abandonment on unmount
  useEffect(() => {
    return () => {
      if (companyFormMethods.formState.isDirty && !companyFormSaved) {
        track(AnalyticsEvents.COMPANY_DETAILS_COMPLETION_ABANDONED, {
          fieldsChanged: Array.from(companyFormChangedFields),
        });
      }
    };
  }, [
    companyFormMethods.formState.isDirty,
    companyFormSaved,
    companyFormChangedFields,
    track,
  ]);

  const submitUserAndCompanyData = async () => {
    const [isUserDataValid, isCompanyDataValid] = await Promise.all([
      userFormMethods.trigger(),
      companyFormMethods.trigger(),
    ]);

    if (!isUserDataValid || !isCompanyDataValid || !userData?.message) return;

    const updatedUserFormData = userFormMethods.getValues();
    const updatedCompanyFormData = companyFormMethods.getValues();

    const userUpdateData = {
      name: updatedUserFormData.user_name,
      phone: updatedUserFormData.user_phone,
      role: updatedUserFormData.user_role,
      language: userData.message.language,
    };

    try {
      if (isCompanyAdmin && userData.message.company_id) {
        const companyUpdateData = {
          name: updatedCompanyFormData.company_name,
          email: updatedCompanyFormData.company_email,
          phone: updatedCompanyFormData.company_phone,
        };

        const [userUpdateResult, companyUpdateResult]: ResponseObject[] =
          await Promise.all([
            updateUser(userData.message.id as string, userUpdateData),
            updateCompany(
              userData.message.company_id as string,
              companyUpdateData
            ),
          ]);

        if (
          userUpdateResult.status === 'success' &&
          companyUpdateResult.status === 'success'
        ) {
          displayMessage(
            t('User and company details were updated.'),
            'success'
          );
          track(AnalyticsEvents.SETTINGS_UPDATE);
          setCompanyFormSaved(true);

          const agentsResult = await getCompanyAgents();
          const agentIds = (
            (agentsResult?.message ?? []) as Array<{ id?: string }>
          )
            .map(a => a.id)
            .filter(Boolean) as string[];
          await Promise.all(
            agentIds.map((agentId: string) =>
              updateAgent(agentId, {
                company: companyUpdateResult.message.name,
              })
            )
          );

          queryClient.invalidateQueries({ queryKey: ['user'] });
          queryClient.invalidateQueries({ queryKey: ['company'] });
          queryClient.invalidateQueries({ queryKey: ['agents'] });
        }
      } else {
        const userUpdateResult: ResponseObject = await updateUser(
          userData.message.id as string,
          userUpdateData
        );

        if (userUpdateResult.status === 'success') {
          displayMessage(t('User details were updated.'), 'success');
          track(AnalyticsEvents.SETTINGS_UPDATE);
          queryClient.invalidateQueries({ queryKey: ['user'] });
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        displayMessage(err.message, 'error');
      }
    }
  };

  return (
    <Page title="Elora | Settings">
      <Stack
        spacing={2.5}
        sx={{
          width: '100%',
          maxWidth: DeviceWidth.LG,
          margin: '0 auto',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0px 10px 26px rgba(15, 23, 42, 0.06)',
            bgcolor: '#FFFFFF',
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={{ xs: 2.5, lg: 3 }}
            alignItems="stretch"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '1.4rem', md: '1.6rem' },
                  fontWeight: 700,
                  color: '#0F172A',
                  mb: 2,
                }}
              >
                {t('Settings')}
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormProvider {...userFormMethods}>
                    <UserForm isLoading={isLoading} />
                  </FormProvider>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormProvider {...companyFormMethods}>
                    <CompanyForm
                      isLoading={isLoading}
                      canEdit={isCompanyAdmin}
                    />
                  </FormProvider>
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
                sx={{ mt: 2.5 }}
              >
                {/* A partner-iframe user has no B2C account, so logging out
                    would drop them on a sign-in page they cannot complete and
                    strand the frame. They leave by closing the cockpit. */}
                {!isPartnerSession && (
                  <Modal
                    callback={logout}
                    confirmText={t('Logout')}
                    denyText={t('Cancel')}
                    description={t(
                      'This action closes your current session, and safely exits you from the app.'
                    )}
                    mainPromptText={t('Do you really want to exit the app?')}
                    openModalButtonText={t('Logout')}
                  />
                )}
                <StyledButton
                  type="button"
                  onClick={submitUserAndCompanyData}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  {t('Save')}
                </StyledButton>
              </Stack>
            </Box>

            <BillingSupportLinks
              sx={{ width: { xs: '100%', lg: 260 }, justifyContent: 'center' }}
            />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0px 10px 26px rgba(15, 23, 42, 0.06)',
            bgcolor: '#FFFFFF',
            p: { xs: 2, md: 3 },
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.4rem' },
              fontWeight: 700,
              color: '#0F172A',
              mb: 0.5,
            }}
          >
            {t('Website embed')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#64748B', mb: 2.5, lineHeight: 1.5, maxWidth: 720 }}
          >
            {t(
              'Add Elora to your website so visitors can open the app in a modal without leaving your page.'
            )}
          </Typography>
          <EmbedSnippet />
        </Paper>
      </Stack>
    </Page>
  );
};

export default Settings;
